/**
 * The user's stamping quota: the contract with `heru-fiscal-engine` and what the
 * web says about it. It is the ONLY place that knows the shape of `GET /cfdi/cupo`,
 * of the 429 `CFDI_CUOTA_AGOTADA` and of the 403 `CFDI_FUNCION_FUERA_DEL_PLAN`; the
 * wizard paywall, the plan chip of the home, the stamping error, the plans screen and
 * the upsell of a feature read from here.
 *
 * El servidor decide y cuenta. La web solo adelanta lo que el servidor ya va a
 * contestar, para que nadie capture una factura que no se va a poder timbrar.
 * Si la consulta falla, la web no bloquea: el 429 del timbrado sigue siendo la
 * última palabra.
 */

import {
  invoicingFeatureOf,
  isInvoicingFeature,
  isTopSellingPlan,
  sellingPlanIncludes,
  type InvoicingFeature,
} from '../constants/planFeatures';
import type { Periodicity } from '@/shared/catalog/types';

export type QuotaPlanSource = 'default' | 'subscription' | 'inclusion' | 'override';

/** ↔ `GET /api/v1/heru-fiscal-engine/cfdi/cupo`. */
export interface InvoiceQuota {
  plan: {
    code: string;
    name: string;
    source: QuotaPlanSource;
    /**
     * Pending backend: the name of the tax plan that includes this one («RESICO»),
     * only with `source: inclusion`. Without it the web says «tu plan de impuestos».
     */
    included_by?: string;
  };
  window: {
    kind: 'month';
    /** `null`: sin tope. */
    limit: number | null;
    used: number;
    /** Timbrados en vuelo: ya apartaron su lugar aunque todavía no se consuman. */
    reserved: number;
    resets_at: string;
  };
  unlimited: boolean;
  /** Los planes mayores al efectivo, de menor a mayor. Vacío: no hay a dónde subir. */
  upgrade_options: string[];
  /**
   * Why `upgrade_options` is empty although a bigger plan exists. `bought_plan`: the user
   * has a live bought invoicing plan and the sales engine refuses a second one, even when
   * the effective plan is an inclusion (RESICO + Pro). Absent when nothing was hidden.
   */
  upgrade_blocked_reason?: string;
  /** The server could not resolve the plan (sales down): `plan` and `unlimited` are its fail-open, not the user's plan. */
  degraded?: boolean;
  /**
   * What the user can do beyond stamping, grandfathering included (`client_email`,
   * `qr_page`…). Absent from an older server: then nothing is gated in the web.
   */
  features?: string[];
}

export const QUOTA_EXHAUSTED = 'CFDI_CUOTA_AGOTADA';

/** `upgrade_blocked_reason` of a user whose bought plan cannot change from the app yet. */
export const UPGRADE_BLOCKED_BOUGHT_PLAN = 'bought_plan';

/** The 403 of an action the user's plan does not include. */
export const FEATURE_NOT_IN_PLAN = 'CFDI_FUNCION_FUERA_DEL_PLAN';

/** What the 403 `CFDI_FUNCION_FUERA_DEL_PLAN` carries in `details`. */
export interface FeatureNotInPlanDetails {
  feature: string | null;
  planCode: string | null;
  /** Sold plans that include it, lowest first; empty under a plan assigned by heru. */
  upgradeOptions: string[];
  /** `bought_plan` when the plan that sells it was hidden because the user bought one. */
  upgradeBlockedReason: string | null;
}

/** Lo que trae el 429 en `details`. El servidor serializa los detalles como texto. */
export interface QuotaExhaustedDetails {
  limit: number | null;
  used: number | null;
  resetsAt: string | null;
  planCode: string | null;
  upgradeAvailable: boolean;
  /** `bought_plan` when a bigger plan exists but the user's bought plan cannot change yet. */
  upgradeBlockedReason: string | null;
}

/** From here on the plan chip warns that the month is running out. */
const NEAR_LIMIT_THRESHOLD = 0.7;

const MEXICO_CITY = 'America/Mexico_City';

const toNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
};

/** Lo ya timbrado más lo que está timbrándose: lo mismo que cuenta el servidor al apartar. */
export const quotaUsedOf = (quota: InvoiceQuota): number => quota.window.used + quota.window.reserved;

const cappedLimitOf = (quota: InvoiceQuota): number | null => (quota.unlimited ? null : quota.window.limit);

export const isQuotaExhausted = (quota: InvoiceQuota): boolean => {
  const limit = cappedLimitOf(quota);
  return limit !== null && quotaUsedOf(quota) >= limit;
};

/** Unlimited (legacy, a la medida or no cap) is never near its limit. */
export const isQuotaNearLimit = (quota: InvoiceQuota): boolean => {
  const limit = cappedLimitOf(quota);
  return limit !== null && limit > 0 && quotaUsedOf(quota) / limit >= NEAR_LIMIT_THRESHOLD;
};

/** `null` si el error no es el 429 de cupo agotado. */
export const quotaExhaustedOf = (error: unknown): QuotaExhaustedDetails | null => {
  const data = (error as { response?: { data?: { code?: unknown; details?: Record<string, unknown> } } } | null)
    ?.response?.data;
  if (typeof data?.code !== 'string' || data.code.toUpperCase() !== QUOTA_EXHAUSTED) return null;
  const details = data.details ?? {};
  return {
    limit: toNumber(details.limit),
    used: toNumber(details.used),
    resetsAt: typeof details.resets_at === 'string' ? details.resets_at : null,
    planCode: typeof details.plan_code === 'string' ? details.plan_code : null,
    upgradeAvailable: details.upgrade_available !== false && details.upgrade_available !== 'false',
    upgradeBlockedReason: typeof details.upgrade_blocked_reason === 'string' ? details.upgrade_blocked_reason : null,
  };
};

/** `null` when the error is not the 403 of a feature outside the plan. */
export const featureNotInPlanOf = (error: unknown): FeatureNotInPlanDetails | null => {
  const data = (error as { response?: { data?: { code?: unknown; details?: Record<string, unknown> } } } | null)
    ?.response?.data;
  if (typeof data?.code !== 'string' || data.code.toUpperCase() !== FEATURE_NOT_IN_PLAN) return null;
  const details = data.details ?? {};
  return {
    feature: typeof details.feature === 'string' ? details.feature : null,
    planCode: typeof details.plan_code === 'string' ? details.plan_code : null,
    upgradeOptions: Array.isArray(details.upgrade_options)
      ? details.upgrade_options.filter((code): code is string => typeof code === 'string')
      : [],
    upgradeBlockedReason: typeof details.upgrade_blocked_reason === 'string' ? details.upgrade_blocked_reason : null,
  };
};

const monthOf = (now: Date) => now.toLocaleDateString('es-MX', { timeZone: MEXICO_CITY, month: 'long' });

const dayOf = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('es-MX', { timeZone: MEXICO_CITY, day: 'numeric', month: 'long' });
};

const invoicesLabel = (count: number) => (count === 1 ? 'factura' : 'facturas');

export interface QuotaCopy {
  headline: string;
  /** Cuándo se renueva; `null` si el servidor no lo dijo. */
  renewal: string | null;
}

/**
 * La consecuencia en una línea, no el mecanismo. Agotado: «Llegaste a tus 5
 * facturas de octubre». Cerca del tope: cuántas le quedan, que es lo único que
 * cambia lo que va a hacer.
 */
export const quotaCopy = (
  { limit, used, resetsAt }: { limit: number | null; used: number | null; resetsAt: string | null },
  now = new Date(),
): QuotaCopy => {
  const day = resetsAt ? dayOf(resetsAt) : null;
  const renewal = day ? `Se renuevan el ${day}.` : null;
  const month = monthOf(now);

  if (limit === null) return { headline: 'Llegaste al límite de facturas de tu plan', renewal };
  if (limit === 0) return { headline: 'Tu plan no incluye facturas', renewal: null };

  const remaining = used === null ? 0 : Math.max(limit - used, 0);
  if (remaining === 0) {
    return {
      headline:
        limit === 1
          ? `Llegaste a tu factura de ${month}`
          : `Llegaste a tus ${limit} ${invoicesLabel(limit)} de ${month}`,
      renewal,
    };
  }
  return {
    headline: `Te ${remaining === 1 ? 'queda' : 'quedan'} ${remaining} de tus ${limit} ${invoicesLabel(limit)} de ${month}`,
    renewal,
  };
};

/** «Plus, 100 facturas al mes» without the name: how many invoices the effective plan gives. */
export const planLimitLine = (quota: InvoiceQuota) => {
  const limit = cappedLimitOf(quota);
  return limit === null ? 'sin límite de facturas' : `${limit} ${invoicesLabel(limit)} al mes`;
};

export type QuotaStatus = 'normal' | 'near_limit' | 'exhausted' | 'unlimited';

/**
 * Whether the answer is a plan the user actually has. `degraded` is the server's
 * fail-open, and an unlimited `default` is its answer for someone the quota does
 * not apply to yet: painting either would tell the user something false.
 */
const describesUserPlan = (quota: InvoiceQuota) =>
  !quota.degraded && !(quota.unlimited && quota.plan.source === 'default');

/** Where the plan comes from, when the user did not buy it. */
const planOriginOf = ({ source, included_by }: InvoiceQuota['plan']): string | null => {
  if (source === 'inclusion') {
    // plans_service names often start with «Plan» («Plan RESICO»): drop it so the line doesn't say it twice.
    const taxPlan = included_by?.replace(/^plan\s+/i, '').trim();
    return taxPlan ? `Incluido en tu plan ${taxPlan}` : 'Incluido en tu plan de impuestos';
  }
  return source === 'override' ? 'Asignado por heru' : null;
};

export interface PlanStatusView {
  status: QuotaStatus;
  used: number;
  /** `null`: no cap. */
  limit: number | null;
  /** The chip's line: the usage of the month, or what the plan gives while nothing is used yet (never a «0 de 5»). */
  headline: string | null;
  /** The exact count the sheet shows above its bar. */
  usage: string;
  renewal: string | null;
  origin: string | null;
}

/** What the plan chip of the home and its sheet say. `null` when there is no plan to show. */
export const planStatusOf = (quota: InvoiceQuota): PlanStatusView | null => {
  if (!describesUserPlan(quota)) return null;
  const limit = cappedLimitOf(quota);
  const origin = planOriginOf(quota.plan);

  if (limit === null) {
    const used = quotaUsedOf(quota);
    return {
      status: 'unlimited',
      used,
      limit,
      headline: used > 0 ? `${used} ${invoicesLabel(used)} este mes` : null,
      usage: 'Sin límite de facturas',
      renewal: null,
      origin,
    };
  }

  const used = Math.min(quotaUsedOf(quota), limit);
  const day = quota.window.resets_at ? dayOf(quota.window.resets_at) : null;
  const usage =
    limit === 0 ? 'Tu plan no incluye facturas' : `${used} de ${limit} ${invoicesLabel(limit)} este mes`;
  const status: QuotaStatus = isQuotaExhausted(quota)
    ? 'exhausted'
    : isQuotaNearLimit(quota)
      ? 'near_limit'
      : 'normal';
  return {
    status,
    used,
    limit,
    headline: used === 0 && limit > 0 ? planLimitLine(quota) : usage,
    usage,
    renewal: day && limit > 0 ? `Se renuevan el ${day}.` : null,
    origin,
  };
};

export const quotaCopyOf = (quota: InvoiceQuota, now = new Date()): QuotaCopy =>
  quotaCopy(
    { limit: cappedLimitOf(quota), used: quotaUsedOf(quota), resetsAt: quota.window.resets_at },
    now,
  );

/** Lo que viaja en los eventos del cupo: el plan, el tope y lo usado tal como lo cuenta el servidor. */
export const quotaSnapshotOf = (quota: InvoiceQuota) => ({
  planCode: quota.plan.code,
  limit: cappedLimitOf(quota),
  used: quotaUsedOf(quota),
});

const INVOICING_PLAN_SLUG_PREFIX = 'facturacion_';

/**
 * El código de plan (`basico`) y el slug de su producto en `plans_service`
 * (`facturacion_basico`) son la misma llave escrita de dos formas: `upgrade_options`
 * habla en códigos y el catálogo de STE en slugs.
 */
export const invoicingPlanSlug = (planCode: string) => `${INVOICING_PLAN_SLUG_PREFIX}${planCode}`;

export const invoicingPlanCodeOf = (slug: string) => slug.replace(INVOICING_PLAN_SLUG_PREFIX, '');

export const isInvoicingPlanSlug = (slug: string | undefined): boolean =>
  Boolean(slug?.startsWith(INVOICING_PLAN_SLUG_PREFIX));

const DAY_MS = 86_400_000;

/**
 * What buying an invoicing plan on `today` (`AAAA-MM-DD`, Mexico City) covers, as
 * heru-fiscal-engine counts it (`invoiceplan.PeriodsAt`): a paid plan's month starts on
 * the purchase day and renews on that day, clamped like Stripe (bought on the 31st, it
 * renews on Feb 28); an annual plan is twelve of those months. `to` is the last day
 * covered, the day before the renewal. Dates are UTC midnights of civil days.
 */
export const invoicingPlanCoverageOf = (periodicity: Periodicity, today: string) => {
  const [year, month, day] = today.split('-').map(Number);
  const months = periodicity === 'annual' ? 12 : 1;
  const daysInRenewalMonth = new Date(Date.UTC(year, month - 1 + months + 1, 0)).getUTCDate();
  const renewal = Date.UTC(year, month - 1 + months, Math.min(day, daysInRenewalMonth));
  return { from: new Date(Date.UTC(year, month - 1, day)), to: new Date(renewal - DAY_MS), renewalDay: day };
};

/** The product slug of the lowest sold plan that includes `feature`; `null` for none or an unknown one. */
export const featurePlanSlugOf = (feature: string | undefined): string | null =>
  feature && isInvoicingFeature(feature) ? invoicingPlanSlug(invoicingFeatureOf(feature).plan) : null;

/**
 * A bought plan the user cannot change yet. The sales engine refuses a second invoicing
 * plan (412) until upgrading with credit exists, so the server offers nothing above it,
 * and that is not «el plan con más facturas». `upgrade_blocked_reason` is the server's
 * word; an older server without it is read from a subscription below the top plan.
 */
export const isPlanChangeUnavailable = (quota: InvoiceQuota): boolean =>
  !quota.degraded &&
  quota.upgrade_options.length === 0 &&
  (quota.upgrade_blocked_reason === UPGRADE_BLOCKED_BOUGHT_PLAN ||
    (quota.plan.source === 'subscription' && !quota.unlimited && !isTopSellingPlan(quota.plan.code)));

/**
 * Still on the free plan everyone starts with, with plans to buy: the only user the
 * onboarding sells an invoicing plan to. A bought, included (tax plan) or overridden
 * plan is not offered another one there, and a degraded answer offers nothing.
 */
export const isOnFreePlanWithOffer = (quota: InvoiceQuota): boolean =>
  quota.plan.source === 'default' && !quota.degraded && quota.upgrade_options.length > 0;

export const isFeatureIncludedInPlan = (quota: InvoiceQuota, feature: InvoicingFeature): boolean =>
  !quota.degraded &&
  (quota.plan.source === 'subscription' || quota.plan.source === 'inclusion') &&
  sellingPlanIncludes(quota.plan.code, feature);

/** What the web says instead of an upgrade it cannot sell. */
export const PLAN_CHANGE_UNAVAILABLE = 'Cambiar de plan todavía no está disponible desde la app';

/** The support chat's `entry_point` for «Habla con nosotros» about changing the plan. */
export const PLAN_CHANGE_SUPPORT_ENTRY = 'facturacion_cambio_plan';

/** The chat draft, with the plan already in it so nobody has to ask. */
export const planChangeSupportDraft = (planLabel?: string, targetPlan?: string): string => {
  const current = planLabel ? `tengo el plan ${planLabel}` : 'ya tengo un plan de facturación';
  return `Hola, ${current} y quiero cambiarme a ${targetPlan ?? 'uno con más facturas'}.`;
};

/** Every variant of the sales engine's 412 to a second invoicing plan starts like this. */
const INVOICING_PLAN_ACTIVE_412 = 'ya cuentas con un plan de facturación activo';

/** Whether a cart error is that 412: the user already has an invoicing plan. */
export const isInvoicingPlanAlreadyActive = (error: unknown): boolean => {
  const response = (error as { response?: { status?: unknown; data?: { message?: unknown } } } | null)?.response;
  const message = response?.data?.message;
  return (
    response?.status === 412 &&
    typeof message === 'string' &&
    message.trim().toLowerCase().startsWith(INVOICING_PLAN_ACTIVE_412)
  );
};
